import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from '../api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface UserPermissions {
  // Dashboard permissions
  canAccessDashboard: boolean;
  // Contact permissions
  canAccessContacts: boolean;
  // Acquisitions permissions
  canAccessAcquisitions: boolean;
  // Due diligence permissions
  canAccessDueDiligence: boolean;
  // Pictures permissions
  canAccessPictures: boolean;
  // Documents permissions
  canAccessDocuments: boolean;
  // Clusters permissions
  canAccessClusters: boolean;
  // Buildings permissions
  canAccessBuildings: boolean;
  // Units permissions
  canAccessUnits: boolean;
  // Building attributes permissions
  canAccessBuildingAttributes: boolean;
  // Rental permissions
  canAccessRental: boolean;
  // Helpdesk permissions
  canAccessHelpdesk: boolean;
  // Conversations permissions
  canAccessConversations: boolean;
  // Maintenance permissions
  canAccessMaintenance: boolean;
  // Insurance permissions
  canAccessInsurance: boolean;
  // Renovation permissions
  canAccessRenovation: boolean;
  // Tasks permissions
  canAccessTasks: boolean;
  // Notes permissions
  canAccessNotes: boolean;
  // General module permissions (Bugs & Roadmap)
  canAccessGeneral: boolean;
  // Corporate module permissions
  canAccessCorporate: boolean;
  // Corporate edit permissions (for future granular control)
  canEditCorporate: boolean;
  // Settings permissions
  canAccessSettings: boolean;
  // Unit ID editing permissions (admin-only operation)
  canEditUnitIds: boolean;
}

interface PermissionsContextType {
  permissions: UserPermissions | null;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  hasAnyPermission: (permissions: (keyof UserPermissions)[]) => boolean;
  hasAllPermissions: (permissions: (keyof UserPermissions)[]) => boolean;
  isLoading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const defaultPermissions: UserPermissions = {
  canAccessDashboard: true,
  canAccessContacts: true,
  canAccessAcquisitions: true,
  canAccessDueDiligence: true,
  canAccessPictures: true,
  canAccessDocuments: true,
  canAccessClusters: true,
  canAccessBuildings: true,
  canAccessUnits: true,
  canAccessBuildingAttributes: true,
  canAccessRental: true,
  canAccessHelpdesk: true,
  canAccessConversations: true,
  canAccessMaintenance: true,
  canAccessInsurance: true,
  canAccessRenovation: true,
  canAccessTasks: true,
  canAccessNotes: true,
  canAccessGeneral: true,
  canAccessCorporate: true,
  canEditCorporate: true,
  canAccessSettings: true,
  canEditUnitIds: false, // Default to false for security - admin only
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

interface PermissionsProviderProps {
  children: React.ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { session, isLoaded } = useSession();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserPermissions = async () => {
    if (!session || !isLoaded) {
      setPermissions(defaultPermissions);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(`${API_URL}/roles/users/${session.user.id}/permission`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);

      if (!response.ok) {
        if (response.status === 404) {
          // User has no role assigned, use default permissions
          console.log('[PermissionsContext] User has no assigned role, using default permissions');
          setPermissions(defaultPermissions);
          return;
        }
        throw new Error(`Failed to fetch permissions: ${response.statusText}`);
      }

      let userPermission;
      try {
        userPermission = await response.json();
        console.log('[PermissionsContext] Fetched user permission:', userPermission);
      } catch (err) {
        setPermissions(defaultPermissions);
        return;
      }

      if (userPermission && userPermission.role) {
        const rolePermissions: UserPermissions = {
          canAccessDashboard: userPermission.role.canAccessDashboard || false,
          canAccessContacts: userPermission.role.canAccessContacts || false,
          canAccessAcquisitions: userPermission.role.canAccessAcquisitions || false,
          canAccessDueDiligence: userPermission.role.canAccessDueDiligence || false,
          canAccessPictures: userPermission.role.canAccessPictures || false,
          canAccessDocuments: userPermission.role.canAccessDocuments || false,
          canAccessClusters: userPermission.role.canAccessClusters || false,
          canAccessBuildings: userPermission.role.canAccessBuildings || false,
          canAccessUnits: userPermission.role.canAccessUnits || false,
          canAccessBuildingAttributes: userPermission.role.canAccessBuildingAttributes || false,
          canAccessRental: userPermission.role.canAccessRental || false,
          canAccessHelpdesk: userPermission.role.canAccessHelpdesk || false,
          canAccessConversations: userPermission.role.canAccessConversations || false,
          canAccessMaintenance: userPermission.role.canAccessMaintenance || false,
          canAccessInsurance: userPermission.role.canAccessInsurance || false,
          canAccessRenovation: userPermission.role.canAccessRenovation || false,
          canAccessTasks: userPermission.role.canAccessTasks || false,
          canAccessNotes: userPermission.role.canAccessNotes || false,
          canAccessGeneral: userPermission.role.canAccessGeneral || false,
          canAccessCorporate: userPermission.role.canAccessCorporate || false,
          canEditCorporate: userPermission.role.canEditCorporate || false,
          canAccessSettings: userPermission.role.canAccessSettings || false,
          canEditUnitIds: userPermission.role.canEditUnitIds || false,
        };
        setPermissions(rolePermissions);
      } else {
        setPermissions(defaultPermissions);
      }
    } catch (err) {
      console.error('[PermissionsContext] Error fetching permissions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setPermissions(defaultPermissions);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPermissions();
  }, [session?.id, isLoaded]);

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    if (!permissions) return false;
    return permissions[permission] === true;
  };

  const hasAnyPermission = (permissionList: (keyof UserPermissions)[]): boolean => {
    if (!permissions) return false;
    return permissionList.some(permission => permissions[permission] === true);
  };

  const hasAllPermissions = (permissionList: (keyof UserPermissions)[]): boolean => {
    if (!permissions) return false;
    return permissionList.every(permission => permissions[permission] === true);
  };

  const refreshPermissions = async () => {
    await fetchUserPermissions();
  };

  const contextValue: PermissionsContextType = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    error,
    refreshPermissions,
  };

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};
